import React, {
  createContext,
  type FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type FormStatus = keyof typeof FORM_STATUS;

export const FORM_STATUS = {
  BUSY: 'BUSY',
  READY: 'READY',
} as const;

interface FormStatusProviderProps {
  children: React.ReactNode;
  onStatusChange?: (status: FormStatus) => void;
}

interface FormStatusContextProps {
  formStatus: FormStatus | null;
  setFormStatus: (target: string, status: FormStatus) => void | undefined;
}

const FormStatusContext = createContext<FormStatusContextProps | undefined>(
  undefined,
);

export const FormStatusProvider: FC<FormStatusProviderProps> = ({
  children,
  onStatusChange,
}) => {
  const previousFormStatus = useRef<FormStatus>(FORM_STATUS.READY);
  const [fieldStatusByTarget, setFieldStatusByTarget] = useState<
    Record<string, FormStatus>
  >({});

  const setFormStatus = useCallback((target: string, status: FormStatus) => {
    setFieldStatusByTarget((previous) => ({ ...previous, [target]: status }));
  }, []);

  const currentFormStatus = useMemo(() => {
    const isFormBusy = Object.values(fieldStatusByTarget).some(
      (status) => status === FORM_STATUS.BUSY,
    );
    return isFormBusy ? FORM_STATUS.BUSY : FORM_STATUS.READY;
  }, [fieldStatusByTarget]);

  useEffect(() => {
    if (previousFormStatus.current !== currentFormStatus) {
      previousFormStatus.current = currentFormStatus;
      onStatusChange?.(currentFormStatus);
    }
  }, [currentFormStatus, onStatusChange]);

  const contextValue = useMemo(() => {
    return {
      formStatus: currentFormStatus,
      setFormStatus,
    };
  }, [currentFormStatus, setFormStatus]);

  return (
    <FormStatusContext.Provider value={contextValue}>
      {children}
    </FormStatusContext.Provider>
  );
};

export function useFormStatus() {
  const context = useContext(FormStatusContext);
  if (context === undefined) {
    throw new Error('useFormStatus must be used within a Form Status provider');
  }
  return context;
}
